import {
  LazyQueryExecFunction,
  OperationVariables,
  useMutation
} from '@apollo/client'
import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import LocalAtmOutlinedIcon from '@material-ui/icons/LocalAtmOutlined'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import PolicyIcon from '@material-ui/icons/Policy'
import parse from 'html-react-parser'
import { useRef } from 'react'
import { toast } from 'react-toastify'

import {
  cartDetailsForSearch,
  cartDetailsVar,
  cartMatchesSearch,
  isActiveCartCreation
} from '../../../../cache/cart'
import CustomCounter from '../../../../components/CustomCounter'
import CustomTooltip from '../../../../components/CustomTooltip.tsx'
import {
  ADD_ITEM_TO_CART_MUTATION,
  CREATE_CART_MUTATION
} from '../../../../graphql/cartCrud'
import { addCommaToPrice } from '../../../../helpers/numbers'
import { errorNoCartFound, extractError } from '../../../../helpers/regex'
import { stripHtmlTags } from '../../../../helpers/stripHTML'
import { cleanCartData } from '../../../../helpers/userData'
import { useUnmountInvalidatedGeneration } from '../../../../hooks/useUnmountInvalidatedGeneration'
import { getTranslate, lang } from '../../../../localization'
import {
  AddItemToCartOutput,
  CartResponse,
  CreateCart,
  ItemInput,
  ItemsInCart
} from '../../../../types/cart'
import { Currency } from '../../../../types/currency'
import {
  Occupancy,
  PaymentTermsBalance,
  RatePlanForSearch,
  SearchAvailabilityInput
} from '../../../../types/searchAvailability'
import RoomAvailable from '../RoomAvailable'
import { CompareAtPrice, DiscountBadge } from './CompareAtPrice'
import {
  ToolTipContentForCancelPolicy,
  ToolTipContentForModifyPolicy
} from './PolicyText'
import { totalPriceTooltip } from './PriceText'
import { getBookabilityLabel } from './helpers'
import useStyles from './styles/index.styles'

type Props = {
  ratePlan: RatePlanForSearch
  currency: Currency
  disable: boolean
  itemsInCart: ItemsInCart
  setItemsInCart: React.Dispatch<React.SetStateAction<ItemsInCart>>
  roomTypeCode: string
  searchAvailabilityInput: SearchAvailabilityInput
  occupancy: Occupancy

  getCart: LazyQueryExecFunction<
    {
      cartResponse: CartResponse
    },
    OperationVariables
  >
  loadingForGetCart: boolean
  setSelectedItemHash: React.Dispatch<React.SetStateAction<string>>
  selectedItemHash: string
  cartData: CartResponse | undefined
  paymentTermsBalance: PaymentTermsBalance[]
}

const RatePlanRow = ({
  ratePlan,
  disable,
  roomTypeCode,
  itemsInCart,
  setItemsInCart,
  searchAvailabilityInput,
  occupancy,
  getCart,
  loadingForGetCart,
  selectedItemHash,
  setSelectedItemHash,
  paymentTermsBalance
}: Props) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const latestSearchRef = useRef(searchAvailabilityInput)
  const previousSearchDetailsRef = useRef(
    cartDetailsForSearch(searchAvailabilityInput)
  )
  const createCartGenerationRef = useUnmountInvalidatedGeneration()
  latestSearchRef.current = searchAvailabilityInput
  if (
    !cartMatchesSearch(
      previousSearchDetailsRef.current,
      searchAvailabilityInput
    )
  ) {
    previousSearchDetailsRef.current = cartDetailsForSearch(
      searchAvailabilityInput
    )
    createCartGenerationRef.current += 1
  }

  //CREATE_CART_MUTATION
  const [executeCreateCart] = useMutation<{ createCart: CreateCart }>(
    CREATE_CART_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onError: () => undefined
    }
  )

  const createCart: typeof executeCreateCart = async (options) => {
    const requestGeneration = ++createCartGenerationRef.current
    const requestedCartDetails = cartDetailsForSearch(latestSearchRef.current)
    const result = await executeCreateCart(options)
    if (
      !isActiveCartCreation({
        requestGeneration,
        activeGeneration: createCartGenerationRef.current,
        requestedCartDetails,
        search: latestSearchRef.current
      })
    ) {
      return result
    }

    if (!result.data?.createCart) {
      toast.error(getTranslate('Something went wrong'))
      return result
    }

    const cartID = result.data.createCart.cartID
    setItemsInCart({ cartID, items: [] })
    cartDetailsVar(requestedCartDetails)
    localStorage.setItem('cartDetails', JSON.stringify(requestedCartDetails))
    localStorage.setItem('cartID', cartID)
    getCart({ variables: { cartID } })
    return result
  }

  //ADD_ITEM_TO_CART_MUTATION
  const [addItem, { loading: loadingForAddItem }] = useMutation<{
    addItemToCart: AddItemToCartOutput
  }>(ADD_ITEM_TO_CART_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      if (localStorage.getItem('cartID')) {
        getCart({
          variables: {
            cartID: localStorage.getItem('cartID')
          }
        })
      }
    },
    onError: (err) => {
      if (extractError(err.cause?.extensions) === errorNoCartFound) {
        toast.error(err.message)
        cleanCartData()
        localStorage.removeItem('confirmPrimitiveInput')
        localStorage.removeItem('confirmPrimitiveReservationState')
        localStorage.removeItem('hotelDetails')
        localStorage.removeItem('primitiveData')
        createCart({
          variables: {
            input: {
              channelCode: '',
              currency: searchAvailabilityInput.currency,
              propertyCode: searchAvailabilityInput.propertyCode,
              reservationType: 'DirectGuest'
            }
          }
        })
      } else {
        toast.error(err.message)
        if (localStorage.getItem('cartID')) {
          getCart({
            variables: {
              cartID: localStorage.getItem('cartID')
            }
          })
        }
      }
    }
  })

  return (
    <div
      className={[classes.ratePlan, disable && classes.ratePlanDisabled].join(
        ' '
      )}>
      <div className={classes.left}>
        <div className={classes.ratePlanTitle}>
          <Typography variant="subtitle1" className={classes.iconText}>
            <MeetingRoomIcon className={classes.icon} />
            {`${ratePlan.ratePlanName}`}
          </Typography>
          {!isMobile &&
            ratePlan.pooledAvailability < 10 &&
            ratePlan.pooledAvailability > 0 && (
              <RoomAvailable availableRoom={ratePlan.pooledAvailability} />
            )}
        </div>
        <div className={classes.availibilityContainer}>
          <Typography variant="h6" className={classes.greyFontWeight}>
            {`${getTranslate('Board Type')}: ${getTranslate(
              ratePlan.boardType
            )}`}
          </Typography>
          {isMobile &&
            ratePlan.pooledAvailability < 10 &&
            ratePlan.pooledAvailability > 0 && (
              <RoomAvailable availableRoom={ratePlan.pooledAvailability} />
            )}
        </div>

        <div className={classes.policy}>
          {ToolTipContentForCancelPolicy(ratePlan) != null ? (
            <CustomTooltip
              title={ToolTipContentForCancelPolicy(ratePlan) || <div />}
              placement="top"
              arrow
              disableHoverListener={disable}
              classes={{ tooltip: classes.toolTip, arrow: classes.arrow }}>
              <div className={classes.policy}>
                <PolicyIcon className={classes.redIcon} />
                <Typography variant="h6" className={classes.redtext}>
                  {getTranslate('Cancellation Policy')}
                </Typography>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </div>
            </CustomTooltip>
          ) : (
            <div className={classes.policy}>
              <CheckCircleIcon className={classes.greenIcon} />
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                className={classes.greenIconText}>
                {getTranslate('Free Cancellation')}
              </Typography>
            </div>
          )}
        </div>
        <div className={classes.policy}>
          {ToolTipContentForModifyPolicy(ratePlan) != null ? (
            <CustomTooltip
              title={ToolTipContentForModifyPolicy(ratePlan) || <div />}
              placement="top"
              arrow
              disableHoverListener={disable}
              classes={{ tooltip: classes.toolTip, arrow: classes.arrow }}>
              <div className={classes.tooltipRow}>
                <CreateOutlinedIcon className={classes.yellowIcon} />
                <Typography variant="h5" className={classes.yellowText}>
                  {getTranslate('Modify Policy')}
                </Typography>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </div>
            </CustomTooltip>
          ) : (
            <div className={classes.policy}>
              <CheckCircleIcon className={classes.greenIcon} />
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                className={classes.greenIconText}>
                {getTranslate('Free Modification')}
              </Typography>
            </div>
          )}
        </div>
        <div className={classes.policy}>
          <>
            {ratePlan.ratePolicy ? (
              <CustomTooltip
                title={
                  <div className={classes.main}>
                    <div className={classes.header}>
                      <Typography variant="h6" className={classes.toolTipText}>
                        {getTranslate('Rate Policy')}
                      </Typography>
                    </div>
                    <div
                      className={classes.content}
                      style={{
                        direction: lang === 'fa' ? 'rtl' : 'ltr', // make layout direction dynamic
                        textAlign: lang === 'fa' ? 'right' : 'left', // align text accordingly
                        whiteSpace: 'pre-line', // keep newlines from stripHtmlTags
                        unicodeBidi: 'embed' // handle mixed RTL/LTR properly
                      }}>
                      {parse(
                        stripHtmlTags(ratePlan.ratePolicy).replace(
                          /\n/g,
                          '<br/>'
                        )
                      )}
                    </div>
                  </div>
                }
                placement="top"
                arrow
                disableHoverListener={disable}
                classes={{ tooltip: classes.toolTip, arrow: classes.arrow }}>
                <div className={classes.policy}>
                  <LocalAtmOutlinedIcon className={classes.yellowIcon} />
                  <Typography variant="h6" className={classes.yellowText}>
                    {getTranslate('Rate Policy')}
                  </Typography>
                  <InfoOutlinedIcon className={classes.infoIcon} />
                </div>
              </CustomTooltip>
            ) : (
              <></>
            )}
          </>
        </div>
      </div>
      <div className={classes.right}>
        <div>
          <CompareAtPrice
            price={ratePlan.price}
            className={classes.discountText}
          />
          <div className={classes.totalAmount}>
            <div className={classes.tootalDiscount}>
              <Typography variant="h5" className={classes.tootalAmountText}>
                {getTranslate('Total Amount')}
              </Typography>
              <div className={classes.discount}>
                <DiscountBadge
                  price={ratePlan.price}
                  className={classes.greenText}
                />
              </div>
            </div>
            <CustomTooltip
              title={totalPriceTooltip(ratePlan)}
              placement="top"
              arrow
              disableHoverListener={disable}
              classes={{ tooltip: classes.toolTip, arrow: classes.arrow }}>
              <div className={classes.price}>
                <InfoOutlinedIcon className={classes.priceInfoIcon} />
                <Typography variant="h4" className={classes.blackText}>
                  {`${addCommaToPrice(
                    ratePlan.price.afterDiscount.totalPrice
                  )} ${getTranslate(ratePlan.price.currency || '')}`}
                </Typography>
              </div>
            </CustomTooltip>
          </div>
        </div>
        <div className={classes.counter}>
          <div></div>
          {ratePlan.bookabilityStatus !== 'BOOKABLE' ? (
            <Button variant="text" disabled>
              <Typography variant="h5" className={classes.greyText}>
                {getTranslate(
                  getBookabilityLabel(ratePlan.bookabilityStatus, occupancy)
                )}
              </Typography>
            </Button>
          ) : disable ? (
            <Typography variant="h5" className={classes.greyText}>
              {getTranslate('Wallet Type Mismatch')}
            </Typography>
          ) : (
            <CustomCounter
              setValue={(val) => {
                if (localStorage.getItem('cartID')) {
                  // No wallet can pay for this provider (for example no active creditor for it in
                  // the site's provider-credit wallet). Say so instead of crashing on [0].
                  const paymentTerm = paymentTermsBalance[0]
                  if (!paymentTerm) {
                    toast.error(
                      getTranslate(
                        'No wallet is available to pay for this item'
                      )
                    )
                    return
                  }

                  setSelectedItemHash(ratePlan.hash)
                  const currentProviderCode = ratePlan.providerCode
                  const newItem: ItemInput = {
                    adults: occupancy.adults,
                    arrival: searchAvailabilityInput.arrival,
                    departure: searchAvailabilityInput.departure,
                    extraBeds: occupancy.extraBeds,
                    providerCode: currentProviderCode,
                    ratePlanCode: ratePlan.ratePlanCode,
                    roomTypeCode: roomTypeCode,
                    quantity: val,
                    useAllocated: false,
                    price: ratePlan.price.afterDiscount.totalPrice
                  }
                  const { price, ...restItem } = newItem

                  addItem({
                    variables: {
                      input: {
                        cartID: localStorage.getItem('cartID'),
                        walletID: paymentTerm.walletID,
                        creditorID: paymentTerm.creditorID ?? undefined,
                        itemsInput: [restItem]
                      }
                    }
                  })
                }
              }}
              value={
                ratePlan.count! <= ratePlan.pooledAvailability
                  ? ratePlan.count!
                  : 0
              }
              max={ratePlan.pooledAvailability}
              loading={
                selectedItemHash === ratePlan.hash &&
                (loadingForAddItem || loadingForGetCart)
              }
              disable={
                loadingForAddItem ||
                loadingForGetCart ||
                disable ||
                !itemsInCart.cartID
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default RatePlanRow
